import { useSelector } from 'react-redux';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { DependencyUsage, dependencyUsageFromDepth, isProjectScope } from '../../../../../shared/issuesV2';
import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';

import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotCreateTicketBox } from './FossabotCreateTicketBox';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { FossabotProjectScopePrompt } from './FossabotProjectScopePrompt';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
type Props = {
  /** Switches the drawer to the Projects tab, where each affected project links to project scope. */
  onViewProjects: () => void;
};

export const FossabotRemediationPanel: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const { data, isLoading, isError, error } = useGitHubAppInstallation('fossabot');
  const { mutate: startInstall } = useStartGitHubAppInstall();

  /*
    fossabot upgrades a dependency by editing the manifest, so it can only reach
    direct dependencies. Transitive ones fail in the fossabot service after the
    click, so the button is disabled up front instead. A project we cannot find
    in the issue, or one with an unknown depth, fails open and stays clickable.
  */
  const project = isProjectScope(scope) ? issue?.projects.find(({ id }) => id === scope.id) : null;
  const isTransitive = dependencyUsageFromDepth(project?.depth ?? null) === DependencyUsage.Deep;

  // !isError guards a stale-success refetch: react-query keeps the last
  // successful `data` around while a background refetch errors.
  const isConnected = Boolean(data?.installed) && !isError;
  const noFixType = useFossabotNoFix();
  const prFlow = useFossabotPrFlow(isConnected);

  /*
    While the connection status is loading, render no box; it resolves in a
    moment and the banner already covers that state. With no fix to offer,
    neither box has anything to show, so both give way to the unfixable state.
    Not connected, or the status check failed and we cannot tell: the
    create-ticket upgrade options, which do not need fossabot at all.
  */
  const renderBox = (): ReactNode => {
    if (isLoading) {
      return null;
    }
    if (noFixType !== null) {
      return <FossabotNoFixBox type={noFixType} />;
    }
    if (isConnected) {
      return (
        <>
          {prFlow && (
            <FossabotFixBox
              status={prFlow.status}
              vcsUrl={prFlow.vcsUrl}
              fossabotUrl={prFlow.fossabotUrl}
              onCreatePR={prFlow.onCreatePR}
              errorMessage={prFlow.errorMessage}
              onRetry={prFlow.onRetry}
              isUpgradeUnavailable={isTransitive}
            />
          )}
          <FossabotOtherFixesBox isInitiallyOpen={isTransitive} />
        </>
      );
    }
    return <FossabotCreateTicketBox />;
  };

  /*
    Outside project scope (global / release group) a fossabot PR cannot be
    created (it targets exactly one repository), so the connection banner and
    fix box give way to a prompt pointing the user at project scope, regardless
    of connection state.
  */
  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          {isProjectScope(scope) ? (
            <>
              <FossabotConnectionBanner
                isConnected={isConnected}
                isLoading={isLoading}
                isError={isError}
                errorMessage={getErrorMessage(error, 'Could not check fossabot connection status.')}
                onConnect={() => startInstall('fossabot')}
              />
              {renderBox()}
            </>
          ) : (
            <>
              <FossabotProjectScopePrompt onViewProjects={onViewProjects} />
              {noFixType !== null ? <FossabotNoFixBox type={noFixType} /> : <FossabotOtherFixesBox isInitiallyOpen />}
            </>
          )}
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
